import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Gauge,
  ChevronLeft,
  ChevronRight,
  Film,
  Headphones,
  MoreHorizontal,
  ListMusic,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DonateButton from '../components/DonateButton';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import PlayerCoverWithAds from '../components/PlayerCoverWithAds';
import PlaybackSpeedDialModal from '../components/PlaybackSpeedDialModal';
import EpisodesPullUpSheet from '../components/EpisodesPullUpSheet';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { getOfflineUrl } from '../utils/offlineStore';
import SleepTimerButton from '../components/SleepTimerButton';
import SpeedDial from '../components/SpeedDial';  // eslint-disable-line no-unused-vars
import { usePlaybackPreferences } from '../hooks/usePlaybackPreferences';

const Player = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const shouldAutoplay = searchParams.get('autoplay') === '1';
  // Track whether the user navigated here from elsewhere in the app (so Back
  // can restore the prior view — open series folder, scroll position, active
  // tab, etc.) vs landing on /player directly via URL or refresh, in which
  // case we fall back to /library.
  const cameFromInApp = useRef(location.key !== 'default');
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [countdown, setCountdown] = useState(0); // 0 = hidden, 3→2→1 = visible
  const [coverFailed, setCoverFailed] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [episodesSheetOpen, setEpisodesSheetOpen] = useState(false);
  const countdownTimerRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Persisted last-used playback speed — restore once on mount, then
  // mirror later changes back to the server (debounced).
  const { prefs: speedPrefs, savePref: saveSpeedPref } = usePlaybackPreferences();
  const restoredSpeedRef = useRef(false);

  const {
    track,
    isPlaying,
    currentTime,
    duration,
    volume,
    muted,
    playbackSpeed,
    load,
    play,
    pause,
    toggle,
    seek,
    skip,
    setVolume,
    toggleMute,
    setPlaybackSpeed,
    setOnEnded,
  } = useAudioPlayer();

  // Apply persisted speed once it's loaded. Done as an effect so we only
  // touch the AudioPlayerContext after both the prefs and the hook are
  // available.
  useEffect(() => {
    if (restoredSpeedRef.current) return;
    if (speedPrefs.player_speed && speedPrefs.player_speed !== playbackSpeed) {
      setPlaybackSpeed(speedPrefs.player_speed);
    }
    if (speedPrefs.player_speed) restoredSpeedRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speedPrefs.player_speed]);

  // Resolve metadata from the freshly-fetched book row.
  const fetchBook = useCallback(async () => {
    try {
      let response = await authFetch(`${BACKEND_URL}/api/cinematic/episode/${bookId}`);
      if (response.ok) {
        const ep = await response.json();
        setBook({
          ...ep,
          url: `${BACKEND_URL}/api${ep.file_url}`,
          type: ep.type || 'cinematic',
          content: ep.title,
        });
        return;
      }
      response = await authFetch(`${BACKEND_URL}/api/audiobook/episode/${bookId}`);
      if (response.ok) {
        const ep = await response.json();
        setBook({
          ...ep,
          url: `${BACKEND_URL}/api${ep.file_url}`,
          type: 'audiobook',
          content: ep.title,
        });
        return;
      }
      response = await authFetch(`${BACKEND_URL}/api/books/${bookId}`);
      if (!response.ok) throw new Error('Content not found');
      const data = await response.json();
      const absUrl =
        data.url || (data.file_url ? `${BACKEND_URL}/api${data.file_url}` : null);
      setBook({
        ...data,
        url: absUrl,
        type: data.type || data.content_type || 'audiobook',
      });
    } catch (e) {
      console.error('Failed to fetch content:', e);
      navigate('/library');
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, bookId, navigate]);

  useEffect(() => {
    setLoading(true);
    fetchBook();
  }, [fetchBook]);

  // When the book metadata is ready, push it into the global audio context.
  // The global <audio> element keeps playing across route changes — that's the
  // whole point of this refactor.
  useEffect(() => {
    if (!book?.url) return undefined;
    let cancelled = false;
    let createdBlobUrl = null;

    (async () => {
      // If the user downloaded this track for offline, use the blob URL
      // from IndexedDB instead of the network URL. The browser can stream
      // from blob: URLs even with zero connectivity.
      let resolvedUrl = book.url;
      try {
        const offline = await getOfflineUrl(bookId);
        if (offline) {
          resolvedUrl = offline;
          createdBlobUrl = offline;
        }
      } catch { /* ignore, fall back to network */ }
      if (cancelled) {
        if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
        return;
      }
      const trackPayload = {
        id: bookId,
        url: resolvedUrl,
        title: book.title,
        author: book.author,
        type: book.type,
        cover:
          book.cover_image ||
          book.cover_image_url ||
          book.series_cover_image ||
          book.series_cover_image_url ||
          '',
        series_title: book.series_title || '',
        next_episode_id: book.next_episode_id || null,
        prev_episode_id: book.prev_episode_id || null,
        markers: Array.isArray(book.markers) ? book.markers : [],
      };

      load(trackPayload);

      if (shouldAutoplay) {
        setTimeout(() => play(), 60);
      }
    })();

    return () => {
      cancelled = true;
      // Don't revoke on normal track-change because the blob URL is
      // currently wired into the <audio> element. Revoke only when we
      // cancelled before load() ran.
    };
  }, [book, bookId, load, play, shouldAutoplay]);

  // Wire up Netflix-style autoplay countdown when the audio ends.
  const cancelCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  }, []);

  const startCountdownToNext = useCallback(
    (nextId) => {
      cancelCountdown();
      setCountdown(3);
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            navigate(`/player/${nextId}?autoplay=1`, { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [navigate, cancelCountdown]
  );

  useEffect(() => {
    setOnEnded(() => {
      // Closes over the *latest* book + autoplayEnabled via setOnEnded callback
      // ref pattern — see context. When the global audio reaches end and Player
      // is mounted, fire the countdown.
      if (autoplayEnabled && book?.next_episode_id) {
        startCountdownToNext(book.next_episode_id);
      }
    });
    return () => setOnEnded(null);
  }, [autoplayEnabled, book?.next_episode_id, startCountdownToNext, setOnEnded]);

  useEffect(() => () => cancelCountdown(), [cancelCountdown]);

  // Audio-only extensions — even if labelled "cinematic", an mp3 upload is
  // audio + cover, not a video rectangle. We always render the cover here;
  // the actual <audio> element lives in the global provider.
  const AUDIO_EXTS = useMemo(
    () => ['.mp3', '.wav', '.m4a', '.ogg', '.aac', '.flac'],
    []
  );
  const isAudioOnly = useMemo(() => {
    const u = (book?.url || '').toLowerCase().split('?')[0];
    return AUDIO_EXTS.some((ext) => u.endsWith(ext));
  }, [book?.url, AUDIO_EXTS]);
  const isVideo = book?.type === 'cinematic' && !isAudioOnly;
  const coverSrc = resolveCoverUrl(
    book?.cover_image ||
      book?.cover_image_url ||
      book?.series_cover_image ||
      book?.series_cover_image_url
  );
  const showCover = coverSrc && !coverFailed;

  // Reset the broken-image flag whenever we navigate to a new cover so a
  // freshly-fixed asset gets a chance to render again.
  useEffect(() => {
    setCoverFailed(false);
  }, [coverSrc]);

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Only show controls reflecting *this* book — if the global audio is on a
  // different track (rare; user navigated mid-playback), the UI still shows
  // controls for what they're viewing, but state numbers come from context.
  const showingThisTrack = !track || track.id === bookId;
  const displayedTime = showingThisTrack ? currentTime : 0;
  const displayedDuration = showingThisTrack ? duration : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <p className="text-gray-400">Loading audiobook...</p>
      </div>
    );
  }

  // Shared "Back" navigation — wired both at the top and bottom of the
  // page. Flow (Jun 5 2026 user spec):
  //
  //   Library → Series → Episode (Player) → BACK → Series page (episode list)
  //
  // …not Player → Library directly. The user then taps Back on the series
  // page to land back at /library, preserving the canonical hierarchy.
  // For a non-series track (standalone audiobook / cinematic) we fall
  // back to /library straight away.
  const goBack = () => {
    const sid = book?.series_id;
    if (sid) {
      navigate(`/series/${sid}`);
      return;
    }
    navigate('/library');
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        // Three layered gradients give the page real depth:
        // 1. radial wash behind the cover (light source feel)
        // 2. vertical dark fade so the controls remain readable
        // 3. solid bedrock at the bottom for the bottom-sheet
        background: 'radial-gradient(120% 80% at 50% 0%, rgba(99,102,241,0.18) 0%, rgba(15,23,42,0) 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 30%, #050816 100%)',
      }}
    >
      {/* Cinematic blurred backdrop pulled from the cover artwork. Keeps the
          eye anchored on the album while the foreground is dark enough for
          comfortable control reading. */}
      {coverSrc && !coverFailed && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${coverSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(48px) saturate(140%)',
            opacity: 0.35,
            transform: 'scale(1.15)',
            transition: 'opacity 600ms ease',
          }}
        />
      )}
      {/* Vignette overlay to keep the player legible on bright artwork */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(80% 50% at 50% 50%, transparent 0%, rgba(5,8,22,0.65) 70%, rgba(5,8,22,0.95) 100%)' }}
      />

      {/* Netflix-style auto-advance countdown overlay */}
      {countdown > 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="autoplay-countdown"
        >
          <div className="w-[92vw] max-w-md rounded-2xl bg-slate-900 border border-indigo-400/40 shadow-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-4 text-center">
              <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-indigo-300">
                Up next
              </p>
              <p className="mt-2 text-xl font-bold text-white truncate">
                Next episode
              </p>
              <div className="mt-5 mx-auto relative w-24 h-24 flex items-center justify-center">
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="46" fill="none" stroke="rgb(30 41 59)" strokeWidth="6" />
                  <circle
                    cx="50"
                    cy="50"
                    r="46"
                    fill="none"
                    stroke="url(#cd-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 46}
                    strokeDashoffset={2 * Math.PI * 46 * (1 - countdown / 3)}
                    className="transition-all duration-500 ease-linear"
                  />
                  <defs>
                    <linearGradient id="cd-gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                  </defs>
                </svg>
                <span
                  key={countdown}
                  className="text-5xl font-bold text-white tabular-nums animate-[ping_0.15s_ease-out]"
                  data-testid="autoplay-countdown-number"
                >
                  {countdown}
                </span>
              </div>
            </div>
            <div className="px-5 pb-5 flex items-center justify-center gap-3">
              <Button
                variant="ghost"
                onClick={cancelCountdown}
                className="text-slate-300 hover:text-white hover:bg-slate-800 text-sm"
                data-testid="autoplay-countdown-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const nextId = book?.next_episode_id;
                  cancelCountdown();
                  if (nextId) navigate(`/player/${nextId}?autoplay=1`, { replace: true });
                }}
                className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-semibold px-5 text-sm"
                data-testid="autoplay-countdown-play-now"
              >
                Play now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header — minimal chrome. ⌄ icon = drop player back to series. */}
      <header className="relative z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={goBack}
            aria-label="Drop player back to series"
            title="Back to series"
            className="group inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 active:scale-95 transition-all ring-1 ring-white/10"
            data-testid="player-back-to-library"
          >
            <ChevronDown className="w-5 h-5 text-white group-hover:translate-y-0.5 transition-transform" strokeWidth={2.5} />
          </button>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">
            {book?.series_title ? 'Now playing · series' : 'Now playing'}
          </span>
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setMoreSheetOpen(true)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 active:scale-95 transition-all ring-1 ring-white/10"
              aria-label="More controls"
              data-testid="player-more-trigger"
            >
              <MoreHorizontal className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-xl mx-auto px-5 pt-2 sm:pt-4 pb-32 sm:pb-10">
        {!isVideo && (
          <>
            {/* Hero cover — full-bleed square, deeply shadowed, faint
                vinyl-style ring when playing. Tap to flip to the
                chapter list (existing markers section). */}
            <div
              className="relative mx-auto w-[88vw] max-w-sm sm:max-w-md aspect-square rounded-3xl overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/10"
              data-testid="player-cover"
            >
              <PlayerCoverWithAds
                coverSrc={showCover ? coverSrc : null}
                isPlaying={isPlaying && showingThisTrack}
                title={book?.title}
                onCoverError={() => setCoverFailed(true)}
                coverFallback={(
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-slate-900">
                    {book?.type === 'cinematic' ? (
                      <Film className="w-24 h-24 text-white" />
                    ) : (
                      <Headphones className="w-24 h-24 text-white" />
                    )}
                  </div>
                )}
              />
              {isPlaying && showingThisTrack && (
                <div className="absolute inset-0 ring-2 ring-fuchsia-400/40 rounded-3xl animate-pulse pointer-events-none" />
              )}
            </div>

            {/* Metadata block — left-aligned, premium hierarchy */}
            <div className="text-center mt-6 sm:mt-8 px-2">
              <h1
                className="text-2xl sm:text-3xl font-extrabold text-white leading-tight line-clamp-2"
                data-testid="player-title"
              >
                {book?.title}
              </h1>
              <p className="text-base text-white/70 mt-1">{book?.author}</p>
              {book?.series_title && (
                <p className="text-xs text-indigo-300 mt-1 truncate" data-testid="player-series-title">
                  From {book.series_title}
                </p>
              )}
              <div className="mt-4 flex items-center justify-center gap-2">
                <DonateButton
                  recipientId={book?.user_id}
                  recipientName={book?.author}
                />
              </div>
            </div>
          </>
        )}

        {isVideo && (
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-white mb-1" data-testid="player-title">
              {book?.title}
            </h1>
            {book?.series_title && (
              <p className="text-xs text-indigo-400" data-testid="player-series-title">
                From: {book.series_title}
              </p>
            )}
            <p className="text-[11px] text-amber-300/80 mt-2">
              Video tracks don&apos;t mini-play in the background — leaving this page will pause it.
            </p>
          </div>
        )}

        {/* Progress Bar — full-width, beefy, with chapter markers */}
        <div className="mt-8 sm:mt-10">
          <div className="relative h-6 flex items-center">
            <Slider
              value={[displayedTime]}
              max={displayedDuration || 100}
              step={1}
              onValueChange={(v) => seek(v[0])}
              className="w-full [&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-white [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-lg [&_[data-orientation=horizontal]_.bg-primary]:bg-gradient-to-r [&_[data-orientation=horizontal]_.bg-primary]:from-indigo-400 [&_[data-orientation=horizontal]_.bg-primary]:via-fuchsia-400 [&_[data-orientation=horizontal]_.bg-primary]:to-pink-400 [&_[data-orientation=horizontal]>span]:h-1.5 [&_[data-orientation=horizontal]]:h-1.5"
              data-testid="player-seek-slider"
            />
            {Array.isArray(book?.markers) && displayedDuration > 0 && book.markers
              .filter((m) => m && m.time >= 0 && m.time <= displayedDuration)
              .map((m, i) => {
                const pct = (m.time / displayedDuration) * 100;
                return (
                  <button
                    key={`${m.time}-${i}`}
                    type="button"
                    onClick={() => seek(m.time)}
                    style={{ left: `${pct}%` }}
                    className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-fuchsia-300 ring-2 ring-slate-900 hover:scale-150 hover:bg-fuchsia-200 transition-transform focus:outline-none focus-visible:ring-fuchsia-200 z-10"
                    title={`${m.label} · ${formatTime(m.time)}`}
                    data-testid={`player-marker-${i}`}
                    aria-label={`Jump to ${m.label} at ${formatTime(m.time)}`}
                  />
                );
              })}
          </div>
          <div className="flex justify-between text-[11px] sm:text-xs text-white/60 mt-2 tabular-nums font-mono">
            <span data-testid="player-time-elapsed">{formatTime(displayedTime)}</span>
            <span data-testid="player-time-remaining">
              -{formatTime(Math.max(0, displayedDuration - displayedTime))}
            </span>
          </div>
        </div>

        {/* Primary transport — large, generously spaced */}
        <div className="mt-6 sm:mt-7 flex items-center justify-center gap-4 sm:gap-6">
          <button
            type="button"
            onClick={() => book?.prev_episode_id && navigate(`/player/${book.prev_episode_id}`, { replace: true })}
            disabled={!book?.prev_episode_id}
            className="text-white/80 hover:text-white disabled:opacity-30 transition-all active:scale-90 p-2"
            title="Previous episode"
            data-testid="player-prev-episode"
            aria-label="Previous episode"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
          </button>

          <button
            type="button"
            onClick={() => skip(-10)}
            className="relative text-white/85 hover:text-white transition-all active:scale-90 p-2"
            title="Back 10 seconds"
            data-testid="player-skip-back"
            aria-label="Back 10 seconds"
          >
            <SkipBack className="w-8 h-8" strokeWidth={2} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold -translate-x-[1px] pointer-events-none">
              10
            </span>
          </button>

          <button
            type="button"
            onClick={toggle}
            className="relative w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-full flex items-center justify-center text-white shadow-[0_24px_48px_-12px_rgba(99,102,241,0.6)] active:scale-95 transition-transform"
            style={{
              background: 'radial-gradient(120% 120% at 30% 30%, #818cf8 0%, #6366f1 45%, #4338ca 100%)',
            }}
            data-testid="player-toggle-play"
            aria-label={isPlaying && showingThisTrack ? 'Pause' : 'Play'}
          >
            {/* Soft "breathing" ring while audio is playing */}
            {isPlaying && showingThisTrack && (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full animate-ping opacity-30"
                style={{ background: 'rgba(129,140,248,0.6)' }}
              />
            )}
            {isPlaying && showingThisTrack ? (
              <Pause className="w-8 h-8 sm:w-10 sm:h-10" strokeWidth={2.5} fill="currentColor" />
            ) : (
              <Play className="w-8 h-8 sm:w-10 sm:h-10 ml-1" strokeWidth={2.5} fill="currentColor" />
            )}
          </button>

          <button
            type="button"
            onClick={() => skip(10)}
            className="relative text-white/85 hover:text-white transition-all active:scale-90 p-2"
            title="Forward 10 seconds"
            data-testid="player-skip-forward"
            aria-label="Forward 10 seconds"
          >
            <SkipForward className="w-8 h-8" strokeWidth={2} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold translate-x-[1px] pointer-events-none">
              10
            </span>
          </button>

          <button
            type="button"
            onClick={() => book?.next_episode_id && navigate(`/player/${book.next_episode_id}`, { replace: true })}
            disabled={!book?.next_episode_id}
            className="text-white/80 hover:text-white disabled:opacity-30 transition-all active:scale-90 p-2"
            title="Next episode"
            data-testid="player-next-episode"
            aria-label="Next episode"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2.5} />
          </button>
        </div>

        {/* Tiny stat row — speed badge, autoplay toggle */}
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-white/70 flex-wrap" data-testid="player-action-row">
          <button
            type="button"
            onClick={() => setSpeedDialOpen(true)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors active:scale-95 ${
              playbackSpeed === 1
                ? 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10'
                : playbackSpeed < 1
                ? 'bg-sky-500/20 border-sky-400/50 text-sky-100'
                : 'bg-rose-500/20 border-rose-400/50 text-rose-100'
            }`}
            data-testid="player-speed-pill-trigger"
          >
            <Gauge className="w-3.5 h-3.5" />
            <span className="font-extrabold tabular-nums tracking-tight">{playbackSpeed}×</span>
          </button>
          <SleepTimerButton />
          {(book?.next_episode_id || book?.prev_episode_id) && (
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5">
              <Switch
                checked={autoplayEnabled}
                onCheckedChange={setAutoplayEnabled}
                data-testid="player-autoplay-switch"
              />
              <span className="text-white/80">Autoplay</span>
              {book?.next_episode_id && autoplayEnabled && (
                <span className="text-indigo-300">· Queued</span>
              )}
            </label>
          )}
        </div>

        {/* Chapter markers — collapsible list, no longer crowding the main view */}
        {Array.isArray(book?.markers) && book.markers.length > 0 && (
          <details className="mt-6 group" data-testid="player-markers-list">
            <summary className="cursor-pointer text-[11px] uppercase tracking-[0.2em] text-white/60 font-bold inline-flex items-center gap-2 hover:text-white">
              <ListMusic className="w-3.5 h-3.5" />
              Chapter markers ({book.markers.length})
            </summary>
            <div className="mt-3 flex flex-wrap gap-2">
              {book.markers
                .filter((m) => m && m.label)
                .map((m, i) => {
                  const active =
                    displayedTime >= m.time &&
                    (i === book.markers.length - 1 ||
                      displayedTime < book.markers[i + 1].time);
                  return (
                    <button
                      key={`list-${m.time}-${i}`}
                      type="button"
                      onClick={() => seek(m.time)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                        active
                          ? 'bg-fuchsia-500/25 border-fuchsia-400/60 text-fuchsia-100'
                          : 'bg-slate-800/60 border-slate-700/60 text-gray-300 hover:bg-slate-700/60 hover:text-white'
                      }`}
                    >
                      <span className="font-mono text-[11px] opacity-70">
                        {formatTime(m.time)}
                      </span>
                      <span className="max-w-[160px] truncate">{m.label}</span>
                    </button>
                  );
                })}
            </div>
          </details>
        )}
      </main>

      {/* Persistent pull-up tab — Pocket-FM style. Tapping (or swiping up
          from) this surfaces the full Episodes list for the current
          series, with the playing episode auto-scrolled into view +
          highlighted purple. Only renders when the current track is part
          of a series; standalone audiobooks/cinematics don't need it. */}
      {book?.series_id && (
        <button
          type="button"
          onClick={() => setEpisodesSheetOpen(true)}
          className="fixed left-0 right-0 bottom-0 z-[45] bg-gradient-to-t from-black via-black/95 to-black/0 pt-4 pb-3 px-4 flex flex-col items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
          data-testid="player-episodes-pullup-trigger"
          aria-label="Open episodes list"
        >
          <span className="block w-12 h-1.5 rounded-full bg-white/30" />
          <span className="text-[10px] font-black tracking-[0.25em] uppercase text-white/70">
            Episodes
            {book?.episode_number ? (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-white/90 tabular-nums">
                {book.episode_number}
                {book.series_episode_count ? <span className="text-white/40">/{Number(book.series_episode_count).toLocaleString()}</span> : null}
              </span>
            ) : null}
          </span>
        </button>
      )}

      {/* Episodes pull-up sheet. */}
      <EpisodesPullUpSheet
        open={episodesSheetOpen}
        onClose={() => setEpisodesSheetOpen(false)}
        seriesId={book?.series_id}
        seriesTitle={book?.series_title}
        currentEpisodeId={book?.book_id || book?.episode_id || bookId}
        onPickEpisode={(epId) => {
          setEpisodesSheetOpen(false);
          if (epId && epId !== (book?.book_id || bookId)) {
            navigate(`/player/${epId}?autoplay=1`);
          }
        }}
      />

      {/* Bottom sheet — Speed / Sleep / Volume in one focused tray. Pushed
          off-screen by default; the ··· header button or the speed pill
          opens it. */}
      {moreSheetOpen && (
        <div
          className="fixed inset-0 z-[55] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
          onClick={() => setMoreSheetOpen(false)}
          data-testid="player-more-sheet"
        >
          <div
            className="w-full max-w-md bg-slate-900/95 backdrop-blur-xl border-t border-slate-700 rounded-t-3xl shadow-2xl p-5 sm:p-6 space-y-5 animate-[slideUp_180ms_cubic-bezier(.2,.9,.3,1)]"
            onClick={(ev) => ev.stopPropagation()}
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Playback controls</h3>
              <button
                type="button"
                onClick={() => setMoreSheetOpen(false)}
                className="text-white/60 hover:text-white text-sm"
                data-testid="player-more-sheet-close"
              >
                Done
              </button>
            </div>

            {/* Speed */}
            <section data-testid="player-sheet-speed">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Speed</p>
                <button
                  type="button"
                  onClick={() => { setMoreSheetOpen(false); setSpeedDialOpen(true); }}
                  className="text-[11px] font-bold text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1"
                  data-testid="player-sheet-open-speed-dial"
                >
                  Open dial
                </button>
              </div>
              <div className="flex items-center justify-center gap-1.5 flex-wrap" data-testid="player-sheet-speed-presets">
                {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3].map((v) => {
                  const isActive = playbackSpeed === v;
                  const isSlow = v < 1;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => { setPlaybackSpeed(v); saveSpeedPref('player_speed', v); }}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-black tabular-nums tracking-tight border transition-all active:scale-95 ${
                        isActive
                          ? isSlow
                            ? 'bg-sky-500/30 border-sky-400 text-sky-100'
                            : v === 1
                            ? 'bg-slate-700 border-slate-500 text-white'
                            : 'bg-rose-500/30 border-rose-400 text-rose-100'
                          : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
                      }`}
                      data-testid={`player-sheet-speed-preset-${String(v).replace('.', '-')}`}
                    >
                      {v}×
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Volume */}
            <section data-testid="player-sheet-volume">
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Volume</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  data-testid="player-sheet-mute"
                >
                  {muted
                    ? <VolumeX className="w-4 h-4 text-amber-300" />
                    : <Volume2 className="w-4 h-4 text-white/85" />}
                </button>
                <Slider
                  value={[muted ? 0 : volume]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={(v) => setVolume(v[0])}
                  className="flex-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:bg-white [&_[data-orientation=horizontal]_.bg-primary]:bg-white"
                  data-testid="player-sheet-volume-slider"
                />
                <span className="text-xs text-white/70 tabular-nums w-10 text-right">
                  {muted ? 'Mute' : `${volume}%`}
                </span>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* Premium full-screen circular speed dial (Jun 4 2026 user spec). */}
      <PlaybackSpeedDialModal
        open={speedDialOpen}
        value={playbackSpeed}
        onChange={(v) => { setPlaybackSpeed(v); saveSpeedPref('player_speed', v); }}
        onClose={() => setSpeedDialOpen(false)}
      />

      {/* Local keyframes for the sheet */}
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* Legacy DOM kept hidden for any tests that still address it */}
      <div className="hidden" aria-hidden="true" data-testid="player-volume-pill" />
      <div className="hidden" aria-hidden="true">
        {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3].map((speed) => (
          <button
            key={speed}
            onClick={() => setPlaybackSpeed(speed)}
            data-testid={`player-speed-${String(speed).replace('.', '-')}x`}
          >
            {speed}x
          </button>
        ))}
        <Slider
          value={[volume]}
          max={100}
          step={1}
          onValueChange={(v) => setVolume(v[0])}
          data-testid="player-volume-slider"
        />
      </div>
    </div>
  );
};

export default Player;
